import { Temporal } from '@js-temporal/polyfill';
import { Fragment, type CSSProperties, type ReactNode } from 'react';

import { clashLabel, placeLabel, SHIFT_STATE, type Shift } from '../api/crew.ts';
import type { Offer } from '../api/offers.ts';
import type { Task } from '../api/tasks.ts';
import { icons } from '../components/icons.ts';
import { lanes, midnights, percent, placement, ticks, type Span } from '../roster-layout.ts';
import { taskProgress } from '../roster-rows.ts';

// CB-REQ OPS-03 at v1.998, CB-CONV §12 crew part 4B, drawn as the operator's roster design of
// 2026-10-26 (docs/designs/roster-page.md, the "one page" board). A row is a label and a lane; its
// bars are packed into lanes, so two that overlap show as two. Every bar is a button whose name
// says what it is. A task is a progress bar: blue-200 fills it by the people on it, covered is solid
// blue with a tick, done is green and struck through. A shift is drawn in its state's colour while
// it carries a task, and grey with an amber edge while it carries none, its state in its label. An
// offer of places is a pale, dashed amber bar on each recipient's row.

export interface Placed {
  readonly key: string;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly node: ReactNode;
}

const hhmm = (local: string) => local.slice(11, 16);
const times = (w: { localStart: string; localEnd: string }) =>
  `${hhmm(w.localStart)} – ${hhmm(w.localEnd)}`;

/** The heading row: the days, then the hours, over the lanes; at 14 days only the days. */
export function TimelineHead({
  span,
  zone,
  every,
  hours,
}: {
  span: Span;
  zone: string;
  every: number;
  hours: boolean;
}) {
  // Each calendar day in view, cut at its midnight.
  const cuts = [0, ...midnights(span, zone).map((m) => m.at), 100].filter(
    (at, i, all) => i === 0 || at > all[i - 1]!,
  );
  const dates = [
    span.from.toZonedDateTimeISO(zone).toPlainDate().toString(),
    ...midnights(span, zone).map((m) => m.date),
  ];
  return (
    <div className="roster-head" aria-hidden="true">
      <div className="roster-corner">Job / crew</div>
      <div>
        <div className="roster-days">
          {cuts.slice(0, -1).map((at, i) => (
            <div
              key={dates[i]}
              className="roster-day"
              style={{ left: `${at}%`, width: `${cuts[i + 1]! - at}%` }}
            >
              {dayName(dates[i]!)}
            </div>
          ))}
        </div>
        {hours ? (
          <div className="roster-hours">
            {ticks(span, zone, every).map((t) => (
              <div key={t.at} className="roster-hour" style={{ left: `${t.at}%` }}>
                {t.label}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** "Mon 26 Oct", as the timeline's days read. */
export function dayName(date: string): string {
  return Temporal.PlainDate.from(date).toLocaleString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

/** The dashed midnights and the now line, over every lane. */
export function TimelineOverlay({ span, zone }: { span: Span; zone: string }) {
  const now = Temporal.Now.instant();
  const inView =
    Temporal.Instant.compare(now, span.from) >= 0 && Temporal.Instant.compare(now, span.to) < 0;
  return (
    <div className="roster-overlay" aria-hidden="true">
      {midnights(span, zone)
        .filter((m) => m.at > 0 && m.at < 100)
        .map((m) => (
          <div key={m.date} className="roster-daybreak" style={{ left: `${m.at}%` }} />
        ))}
      {inView ? <div className="roster-now" style={{ left: `${percent(span, now)}%` }} /> : null}
    </div>
  );
}

/** A heading across the timeline: Tasks, then People, with what they hold. */
export function GroupRow({ label, meta }: { label: string; meta: string }) {
  return (
    <div className="roster-group">
      <span className="roster-group-label">
        <span className="roster-group-mark" aria-hidden="true" />
        <span className="roster-group-title">{label}</span>
        <span className="roster-group-meta">{meta}</span>
      </span>
    </div>
  );
}

export function TrackRow({
  label,
  kind,
  items,
  cols,
  track,
  overlay,
  behind,
}: {
  label: ReactNode;
  kind: 'job' | 'crew';
  items: readonly Placed[];
  /** Grid lines across the lane. */
  cols: number;
  /** OPS-03 at v1.9105: where a window may be drawn (the shared gestures' track props). */
  track?: object | undefined;
  /** Over the bars: a window being drawn, the choice of what to make, a dragged bar's chip. */
  overlay?: ReactNode;
  /** CRW-02 at v1.9106: unavailability and leave, drawn behind the bars. */
  behind?: ReactNode;
}) {
  const packed = lanes(items);
  return (
    <div className={`roster-row roster-row-${kind}`}>
      <div className={`roster-label roster-label-${kind}`}>{label}</div>
      <div className="roster-track" {...track}>
        {behind}
        {overlay}
        {(packed.length ? packed : [[]]).map((lane, i) => (
          <div key={i} className="roster-lane" style={{ '--cols': cols } as CSSProperties}>
            {lane.map((item) => (
              <Fragment key={item.key}>{item.node}</Fragment>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Where a bar sits, in percent of the span. */
export function barStyle(span: Span, w: { startsAt: string; endsAt: string }): CSSProperties {
  const { left, width } = placement(span, w.startsAt, w.endsAt);
  return { left: `${left}%`, width: `${width}%` };
}

interface Pickable {
  /** In Select mode: whether it is ticked; undefined outside it. */
  readonly selected?: boolean | undefined;
  readonly onClick: () => void;
  readonly style: CSSProperties;
  /** OPS-03 at v1.9105: the shared gestures' bar props, to move and resize it. */
  readonly gesture?: object | undefined;
}

/** A task as a progress bar: n of m, solid when covered, struck through when done. */
export function TaskBar({ task, selected, onClick, style, gesture }: { task: Task } & Pickable) {
  const t = task;
  const { fill, covered, done } = taskProgress(t);
  const count = `${t.filled} of ${t.crewNeeded}`;
  const description = [
    `Task: ${t.description}`,
    t.role?.name ?? '',
    times(t.window),
    t.venue?.name ?? '',
    `${count} people`,
    covered ? 'Covered' : '',
    t.fill === 'over' ? 'Over-filled' : '',
    done ? 'Done' : '',
    t.offSold ? 'Off sold times' : '',
    selected === undefined ? '' : selected ? 'Ticked' : 'Not ticked',
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <button
      type="button"
      className="roster-bar task-bar"
      data-covered={covered ? 'true' : undefined}
      data-done={done ? 'true' : undefined}
      data-selected={selected ? 'true' : undefined}
      aria-pressed={selected}
      style={{ ...style, '--fill': `${fill * 100}%` } as CSSProperties}
      title={description}
      aria-label={description}
      {...gesture}
      onClick={onClick}
    >
      <span className="task-fill" aria-hidden="true" />
      {covered || done ? (
        <icons.added size={11} strokeWidth={3} aria-hidden className="relative shrink-0" />
      ) : null}
      <span className="roster-bar-label">
        {t.description} · {count}
      </span>
    </button>
  );
}

/** Its state in a word, confirmed told apart from accepted (CRW-16). */
export function stateWord(s: Pick<Shift, 'state' | 'confirmedAt'>): string {
  return s.state === 'accepted' && s.confirmedAt ? 'Confirmed' : SHIFT_STATE[s.state];
}

/** "Confirmed · 05:30 – 17:30 · J-2618", as the design's bars read. */
export function shiftLabel(shift: Shift): string {
  const job = shift.bookedForJob;
  const where = job
    ? `J-${job.number}${job.cancelled ? ' cancelled' : ''}`
    : shift.held
      ? shift.lapsed
        ? 'lapsed'
        : 'job to come'
      : placeLabel(shift);
  if (shift.held && !job) {
    return `Held day · ${stateWord(shift).toLowerCase()} · ${where}`;
  }
  const state =
    shift.state === 'accepted' && !shift.confirmedAt
      ? shift.toConfirm
        ? 'Due to confirm'
        : 'Accepted, to confirm'
      : stateWord(shift);
  return `${state} · ${times(shift.window)} · ${where}`;
}

export function ShiftBar({
  shift,
  selected,
  onClick,
  style,
  gesture,
  overTimeOff,
}: {
  shift: Shift;
  /** CRW-02 at v1.9106: it overlaps the person's unavailability or leave; outlined, never refused. */
  overTimeOff?: string | undefined;
} & Pickable) {
  const job = shift.bookedForJob;
  // JOB-10 at v1.962: a cancelled job's shift stands until the office rebooks, declines or cancels it.
  const flagged = job?.cancelled ?? false;
  const clash = shift.clashes.length > 0;
  // CMP-07 at v1.965: someone on it lacks a ticket or induction it needs.
  const gaps = shift.complianceGaps.map((g) => g.name).join(', ');
  // CMP-02 at v1.967: what they have not signed; in the description, never a refusal.
  const toSign = shift.safetyGaps.map((g) => g.title).join(', ');
  const description = [
    `${shift.crewMember?.name ?? 'Unfilled'}, ${shift.role?.name ?? (shift.held ? 'held day' : 'no role')}`,
    shift.held
      ? shift.lapsed
        ? 'Held day, lapsed — no place given'
        : `Held day, venue to come${shift.toAssign ? ' — place it now' : ''}`
      : `At ${placeLabel(shift)}`,
    job ? `for J-${job.number} ${job.name}` : '',
    times(shift.window),
    stateWord(shift),
    shift.state === 'accepted' && !shift.confirmedAt
      ? shift.toConfirm
        ? 'To confirm now'
        : 'Not confirmed yet'
      : '',
    shift.untasked ? 'No task' : `Tasks: ${shift.tasks.map((t) => t.description).join(', ')}`,
    flagged ? 'Its job was cancelled: rebook it, record they pulled out, or cancel it' : '',
    clash ? `Clashes with ${shift.clashes.map(clashLabel).join(', ')}` : '',
    gaps ? `Needs ${gaps}` : '',
    toSign ? `To sign: ${toSign}` : '',
    overTimeOff ? `Over ${overTimeOff}` : '',
    selected === undefined ? '' : selected ? 'Ticked' : 'Not ticked',
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <button
      type="button"
      className="roster-bar"
      data-state={shift.state}
      data-confirmed={shift.state === 'accepted' && shift.confirmedAt ? 'true' : undefined}
      data-clash={clash ? 'true' : undefined}
      data-gaps={gaps ? 'true' : undefined}
      data-unconfirmed={shift.state === 'accepted' && !shift.confirmedAt ? 'true' : undefined}
      data-due={shift.toConfirm ? 'true' : undefined}
      data-flagged={flagged ? 'true' : undefined}
      data-kind={job ? undefined : shift.held ? 'held' : 'location'}
      data-untasked={shift.untasked ? 'true' : undefined}
      data-time-off={overTimeOff ? 'true' : undefined}
      data-selected={selected ? 'true' : undefined}
      aria-pressed={selected}
      style={style}
      title={description}
      aria-label={description}
      {...gesture}
      onClick={onClick}
    >
      <span className="roster-bar-label">{shiftLabel(shift)}</span>
    </button>
  );
}

/** CRW-04 at v1.994: an offer of places this person has yet to answer. */
export function OfferBar({
  offer,
  onClick,
  style,
  gesture,
}: {
  offer: Offer;
  onClick: () => void;
  style: CSSProperties;
  gesture?: object | undefined;
}) {
  const o = offer;
  const job = o.bookedForJob;
  const held = !o.venue && !o.location;
  const places = `${o.places} ${o.places === 1 ? 'place' : 'places'}, ${o.taken} taken`;
  const description = [
    `Offer of places: ${o.role?.name ?? 'no role'}`,
    job ? `J-${job.number} ${job.name}` : held ? 'Held days' : placeLabel(o),
    times(o.window),
    places,
    'Not answered. Open the offer',
  ].join(' · ');
  return (
    <button
      type="button"
      className="roster-bar"
      data-offer="true"
      style={style}
      title={description}
      aria-label={description}
      {...gesture}
      onClick={onClick}
    >
      <span className="roster-bar-label">
        Offer · {o.role?.name ?? (job ? `J-${job.number}` : 'Held')} · {places}
      </span>
    </button>
  );
}

/** "Melbourne time": the zone a window is read in, as the design names it. */
export function zoneName(zone: string): string {
  return `${(zone.split('/').at(-1) ?? zone).replace(/_/g, ' ')} time`;
}

/** Two initials, as the crew rows show them. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return (
    (parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '')
  ).toUpperCase();
}
